import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Calendar, Clock, Plus, Hash } from "lucide-react";

const weekDays = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function eventClasses(type, priority) {
  if (type === "exchange") {
    return "bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300";
  }
  if (type === "serial") {
    return "bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300";
  }
  // task
  switch (priority) {
    case "Hoch":
    case "high":
      return "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300";
    case "Mittel":
    case "medium":
      return "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300";
    default:
      return "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300";
  }
}

export function TaskCalendar({
  calendarData,
  calendarMonth,
  setCalendarMonth,
  loading = false,
  onCreateEvent,
  onEventClick,
}) {
  const year = calendarData?.year ?? (calendarMonth?.getFullYear?.() ?? new Date().getFullYear());
  const month = calendarData?.month ?? (calendarMonth?.getMonth?.() ?? new Date().getMonth());
  const monthLabel =
    calendarData?.monthLabel ||
    new Date(year, month, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  const days = calendarData?.calendarDays ?? [];

  const handlePrevMonth = () => setCalendarMonth?.(new Date(year, month - 1, 1));
  const handleNextMonth = () => setCalendarMonth?.(new Date(year, month + 1, 1));

  return (
    <Card data-testid="task-calendar">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Kalender
        </CardTitle>
        <div className="flex items-center gap-3 flex-wrap">
          {onCreateEvent && (
            <Button size="sm" onClick={onCreateEvent} data-testid="calendar-new-event-btn">
              <Plus className="w-4 h-4 mr-2" />
              Neuer Termin
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={handlePrevMonth} data-testid="calendar-prev-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-medium min-w-32 text-center" data-testid="calendar-month-label">
              {monthLabel}
            </span>
            <Button variant="outline" size="icon" onClick={handleNextMonth} data-testid="calendar-next-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner" />
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map((day) => (
              <div key={day} className="bg-muted p-2 text-center text-sm font-medium">
                {day}
              </div>
            ))}
            {days.map((item) => {
              if (item.isEmpty) {
                return <div key={item.key} className="h-28 bg-muted/20" />;
              }

              const events = item.events || [];
              return (
                <div
                  key={item.key}
                  className={`h-28 border p-1 overflow-hidden ${item.isToday ? "border-primary border-2" : ""}`}
                  data-testid={`calendar-day-${item.dateKey}`}
                >
                  <div className={`text-sm font-medium ${item.isToday ? "text-primary" : ""}`}>
                    {item.day}
                  </div>
                  <div className="space-y-0.5 mt-1 overflow-y-auto max-h-20">
                    {events.slice(0, 3).map((event, idx) => (
                      <button
                        type="button"
                        key={`${event.id || idx}`}
                        onClick={() => onEventClick && onEventClick(event)}
                        className={`w-full text-left text-xs px-1 py-0.5 rounded truncate ${eventClasses(event.type, event.priority)}`}
                        title={event.title || event.subject}
                        data-testid={`calendar-event-${event.type}-${event.id || idx}`}
                      >
                        {event.type === "exchange" && <Clock className="w-3 h-3 inline mr-1" />}
                        {event.type === "serial" && <Hash className="w-3 h-3 inline mr-1" />}
                        {event.title || event.subject}
                      </button>
                    ))}
                    {events.length > 3 && (
                      <div className="text-xs text-muted-foreground text-center">
                        +{events.length - 3} mehr
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
